import { Observable, Subscription } from "rxjs";
import { ModalOptions, NzModalRef, NzModalState } from "ng-zorro-antd/modal";

export interface UserDialogOriginSnapshot {
    readonly instance: object;
    readonly isOpen: boolean;
    readonly productLicenseId: number;
    readonly revision: number;
    readonly field: string;
    readonly defaultRole?: string;
}
// Local references and callbacks only; never serialize this into HTTP, routes or storage.
export interface UserDialogOrigin {
    readonly requiresEvidenceUpload?: boolean;
    readonly hideAssessmentRoles?: boolean;
    snapshot(): UserDialogOriginSnapshot;
    changes: Observable<unknown>;
    closeDropdown(): void;
    element: HTMLElement;
    focus(): void;
    launchEvent?: Event;
}

const guarded = { nzKeyboard: false, nzClosable: false, nzMaskClosable: false, nzOkDisabled: true, nzCancelDisabled: true } as const;

export class UserDialogOriginGuard {
    private readonly captured: UserDialogOriginSnapshot | null;
    private readonly subscriptions = new Subscription();
    private readonly releases: (() => void)[] = [];
    private released = false;
    readonly zIndex: number;

    constructor(private readonly origin: UserDialogOrigin | undefined, parents: readonly NzModalRef[],
        private readonly licenseId: number | null, private readonly defaultRole: string | undefined) {
        this.captured = origin ? { ...origin.snapshot() } : null;
        this.zIndex = Math.max(1000, ...parents.map((ref) => Number(ref.getConfig().nzZIndex) || 1000)) + 10;
    }

    isCurrent(): boolean {
        if (!this.origin || !this.captured) return true;
        const now = this.origin.snapshot(), old = this.captured;
        return now.isOpen && old.isOpen && this.origin.element.isConnected && now.instance === old.instance &&
            now.productLicenseId === this.licenseId && old.productLicenseId === this.licenseId && now.revision === old.revision &&
            Number.isSafeInteger(old.revision) && old.revision >= 0 && !!old.field && now.field === old.field &&
            old.defaultRole === this.defaultRole && now.defaultRole === old.defaultRole;
    }

    private sameContext(): boolean {
        if (!this.origin || !this.captured) return true;
        const now = this.origin.snapshot(), old = this.captured;
        return now.isOpen && this.origin.element.isConnected && now.instance === old.instance &&
            now.productLicenseId === old.productLicenseId && now.field === old.field && now.defaultRole === old.defaultRole;
    }

    activate(parents: readonly NzModalRef[], cancel: () => void): void {
        const block = (event: Event) => { event.preventDefault(); event.stopImmediatePropagation(); };
        const elements = new Set<HTMLElement>();
        if (this.origin) {
            this.origin.launchEvent?.preventDefault();
            this.origin.launchEvent?.stopPropagation();
            this.origin.closeDropdown();
            elements.add(this.origin.element);
        }
        for (const parent of parents) {
            const config = parent.getConfig();
            const saved: ModalOptions = {};
            for (const key of Object.keys(guarded) as (keyof typeof guarded)[]) saved[key] = config[key];
            parent.updateConfig(guarded);
            elements.add(parent.getElement());
            this.subscriptions.add(parent.afterClose.subscribe(cancel));
            this.releases.push(() => {
                if (parent.getState() !== NzModalState.CLOSED) parent.updateConfig(saved);
            });
        }
        for (const element of elements) {
            for (const type of ["mousedown", "click", "keydown", "submit"]) element.addEventListener(type, block, true);
            this.releases.push(() => {
                for (const type of ["mousedown", "click", "keydown", "submit"]) element.removeEventListener(type, block, true);
            });
        }
        if (this.origin) this.subscriptions.add(this.origin.changes.subscribe({
            next: () => { if (!this.isCurrent()) cancel(); }, complete: cancel
        }));
    }

    release(restoreFocus: boolean): void {
        if (this.released) return;
        this.released = true;
        this.subscriptions.unsubscribe();
        for (const release of this.releases.reverse()) release();
        if (restoreFocus && this.sameContext()) this.origin?.focus();
    }
}
